import {
  Table,
  TableBody,
  TableCell,
  TableHead,
  TableHeader,
  TableRow,
} from '@/components/ui/table';
import { Badge } from '@/components/ui/badge';
import type { GrClaimRejectionEntry } from '@/lib/api';

interface RejectionTableProps {
  entries: GrClaimRejectionEntry[];
  /** When true, truncates the rejection-message column more aggressively for
   *  compact panels (e.g. the at-a-glance view on AdminGamesPage). */
  compact?: boolean;
}

function formatTime(iso: string): string {
  return new Date(iso).toLocaleString();
}

/**
 * Shared rendering of `gr_claim_rejection_log` rows. Used by:
 *   - AdminGamesPage's "Recent Claim Rejections" panel (compact view)
 *   - AdminRewardLogPage's "Rejected" tab (full view)
 *
 * Columns: Time, User (resolved username), Source (trust level + arche/app
 * id), Game, Event, Score, Code, Message. Username falls back to "unknown"
 * when the userId didn't resolve via batchGetUsernames upstream.
 *
 * Extracted per review I12 — the two surfaces previously rendered the same
 * markup with the same conditionals and column shapes.
 */
export function RejectionTable({ entries, compact = false }: RejectionTableProps) {
  const messageMaxClass = compact ? 'max-w-[20ch]' : 'max-w-[28ch]';
  return (
    <div className="overflow-x-auto">
      <Table>
        <TableHeader>
          <TableRow>
            <TableHead>Time</TableHead>
            <TableHead>User</TableHead>
            <TableHead>Source</TableHead>
            <TableHead>Game</TableHead>
            <TableHead>Event</TableHead>
            <TableHead>Score</TableHead>
            <TableHead>Code</TableHead>
            <TableHead>Message</TableHead>
          </TableRow>
        </TableHeader>
        <TableBody>
          {entries.map((r) => (
            <TableRow key={r.rejectionId}>
              <TableCell className="text-xs whitespace-nowrap">
                {formatTime(r.occurredAt)}
              </TableCell>
              <TableCell className="text-xs">
                {r.username ?? <span className="text-muted-foreground">unknown</span>}
              </TableCell>
              <TableCell className="text-xs">
                <span className="font-medium">{r.trustLevel}</span>
                {r.archeId ? (
                  <>
                    <br />
                    <span className="text-muted-foreground">{r.archeId}</span>
                  </>
                ) : null}
                {r.oauthAppId ? (
                  <>
                    <br />
                    <span className="text-muted-foreground">{r.oauthAppId}</span>
                  </>
                ) : null}
              </TableCell>
              <TableCell className="text-xs">{r.attemptedGameName ?? '—'}</TableCell>
              <TableCell className="text-xs">{r.gameEvent ?? '—'}</TableCell>
              <TableCell className="text-xs">{r.gameScore ?? '—'}</TableCell>
              <TableCell className="text-xs">
                <Badge variant="destructive">{r.rejectionCode}</Badge>
              </TableCell>
              <TableCell
                className={`text-xs ${messageMaxClass} truncate`}
                title={r.rejectionMessage}
              >
                {r.rejectionMessage}
              </TableCell>
            </TableRow>
          ))}
        </TableBody>
      </Table>
    </div>
  );
}
